<template>
  <div class="pie-chart" :class="{ compact }">
    <svg 
      :width="chartSize" 
      :height="chartSize" 
      :viewBox="`0 0 ${chartSize} ${chartSize}`"
      class="pie-svg"
    >
      <g :transform="`translate(${center}, ${center})`">
        <path 
          v-for="slice in slices" 
          :key="slice.class"
          :d="slice.path"
          :fill="slice.color"
          :stroke="strokeColor"
          :stroke-width="compact ? 1 : strokeWidth"
          class="slice"
          :class="{ active: hovered === slice.class }"
          @mouseenter="hovered = slice.class"
          @mouseleave="hovered = null"
        >
          <title>{{ slice.label }}: {{ slice.percentage }}%</title>
        </path>
      </g>
    </svg>
    
    <div v-if="!compact" class="legend">
      <div 
        v-for="slice in slices" 
        :key="`legend-${slice.class}`" 
        class="legend-item"
        :class="{ active: hovered === slice.class }"
        @mouseenter="hovered = slice.class"
        @mouseleave="hovered = null"
      >
        <span class="legend-color" :style="{ backgroundColor: slice.color }"></span>
        <span class="legend-label">{{ slice.label }}:</span>
        <span class="legend-value">{{ slice.percentage }}%</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getColorForClass } from '@/common/mixins/colors'

export default {
  name: 'PieChart',
  
  props: {
    data: {
      type: Array,
      required: true,
      validator: data => data.every(item => 'class' in item && 'percentage' in item)
    },
    size: { type: Number, default: 200 },
    radius: { type: Number, default: 80 },
    colorMap: { type: Object, default: null },
    strokeColor: { type: String, default: '#ffffff' },
    strokeWidth: { type: Number, default: 2 },
    labelFormatter: { type: Function, default: null },
    startAngle: { type: Number, default: -90 },
    compact: { type: Boolean, default: false }
  },

  data() {
    return {
      hovered: null
    }
  },

  computed: {
    chartSize() {
      return this.compact ? 32 : this.size
    },

    chartRadius() {
      return this.compact ? 14 : this.radius
    },

    center() {
      return this.chartSize / 2
    },

    slices() {
      const startRad = (this.startAngle * Math.PI) / 180
      let currentAngle = startRad

      return this.data.map(item => {
        const angle = (item.percentage / 100) * 2 * Math.PI
        const start = currentAngle
        const end = currentAngle + angle
        currentAngle = end

        return {
          path: this.createSlicePath(start, end),
          class: item.class,
          percentage: item.percentage,
          label: this.formatLabel(item.class),
          color: getColorForClass(item.class, this.colorMap)
        }
      })
    }
  },

  methods: {
    createSlicePath(start, end) {
      const r = this.chartRadius
      const x1 = r * Math.cos(start)
      const y1 = r * Math.sin(start)
      const x2 = r * Math.cos(end)
      const y2 = r * Math.sin(end)
      const largeArc = (end - start) > Math.PI ? 1 : 0

      return `M 0 0 L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`
    },

    formatLabel(classValue) {
      if (this.labelFormatter) return this.labelFormatter(classValue)
      return `${this.$t('Class')} ${classValue}`
    }
  }
}
</script>

<style lang="scss" scoped>
.pie-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;

  &.compact {
    display: inline-flex;
    flex-direction: row;
    gap: 0;
    width: auto;
  }
}

// ================================
// SVG
// ================================
.pie-svg {
  display: block;
}

.slice {
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover,
  &.active {
    opacity: 0.8;
  }
}

// ================================
// LEGEND
// ================================
.legend {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
  max-width: 18rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover,
  &.active {
    background: $grey-2;

    body.body--dark & {
      background: $grey-9;
    }
  }
}

.legend-color {
  width: 0.875rem;
  height: 0.875rem;
  border-radius: var(--radius-xs);
  flex-shrink: 0;
}

.legend-label {
  flex: 1;
  font-size: 0.75rem;
  color: $grey-7;

  body.body--dark & {
    color: $grey-5;
  }
}

.legend-value {
  font-size: 0.75rem;
  font-weight: 600;
  color: $grey-9;

  body.body--dark & {
    color: $grey-3;
  }
}
</style>